import { ChatStreamError } from "../api-client.js";
import type { StreamHandlers } from "../api-client.js";
import type { ChatApi } from "../session-store.js";
import type { ChatConfig } from "../types.js";

type Directive =
  | "outage"
  | "outage-network"
  | "rejected"
  | "chat-unavailable"
  | "rate-limit"
  | "answer"
  | "passthrough";

type AnswerConfig = { text: string; sources: unknown[] };

const DEFAULT_ANSWER: AnswerConfig = {
  text: "[dev] Gesimuleerd AI-antwoord.",
  sources: [
    {
      kind: "help-article",
      url: "https://example/1",
      title: "Voorbeeldartikel",
      label: "Voorbeeldartikel",
      excerpt: "Dev bron",
      sourcetag: "Artikel",
      opensInNewTab: false,
    },
  ],
};

export function installChatDevHarness(
  api: ChatApi,
  config: ChatConfig,
): ChatApi {
  if (config.devTools !== true || typeof window === "undefined") return api;

  let queue: Directive[] = [];
  let sticky: Directive | null = null;
  let answer: AnswerConfig = { ...DEFAULT_ANSWER };

  function nextDirective(): Directive {
    if (queue.length) return queue.shift() as Directive;
    return sticky ?? "passthrough";
  }

  function emitOutageSse(handlers: StreamHandlers): void {
    handlers.onEvent({
      event: "error",
      data: JSON.stringify({
        code: "ai_unavailable",
        message: "[dev] Simulated AI outage",
        retryable: true,
        outage: true,
      }),
    });
  }

  function emitRejectedSse(handlers: StreamHandlers): void {
    handlers.onEvent({
      event: "error",
      data: JSON.stringify({
        code: "rejected",
        message: "[dev] Simulated rejection",
        retryable: false,
        outage: false,
      }),
    });
  }

  function emitChatUnavailableSse(handlers: StreamHandlers): void {
    handlers.onEvent({
      event: "error",
      data: JSON.stringify({
        code: "chat_unavailable",
        message: "[dev] Simulated dead chat",
        retryable: false,
        outage: false,
      }),
    });
  }

  function emitAnswer(handlers: StreamHandlers): void {
    handlers.onEvent({
      event: "session",
      data: JSON.stringify({ sessionToken: `dev-${Date.now()}` }),
    });
    handlers.onEvent({
      event: "message-start",
      data: JSON.stringify({ messageId: "dev-answer" }),
    });
    handlers.onEvent({
      event: "message-update",
      data: JSON.stringify({
        messageId: "dev-answer",
        text: answer.text,
        blocks: [
          {
            type: "paragraph",
            inlines: [{ type: "text", value: answer.text }],
          },
        ],
      }),
    });
    handlers.onEvent({
      event: "message-complete",
      data: JSON.stringify({
        messageId: "dev-answer",
        text: answer.text,
        blocks: [
          {
            type: "paragraph",
            inlines: [{ type: "text", value: answer.text }],
          },
        ],
        sources: answer.sources,
        canGiveFeedback: true,
      }),
    });
    handlers.onEvent({
      event: "done",
      data: JSON.stringify({ status: "completed" }),
    });
  }

  async function run(
    real: (
      payload: unknown,
      handlers: StreamHandlers,
      signal: AbortSignal,
    ) => Promise<void>,
    payload: unknown,
    handlers: StreamHandlers,
    signal: AbortSignal,
  ): Promise<void> {
    const directive = nextDirective();
    switch (directive) {
      case "outage":
        emitOutageSse(handlers);
        return;
      case "rejected":
        emitRejectedSse(handlers);
        return;
      case "chat-unavailable":
        emitChatUnavailableSse(handlers);
        return;
      case "outage-network":
        throw new TypeError("[dev] Simulated connection failure");
      case "rate-limit":
        throw new ChatStreamError("[dev] Te veel verzoeken.", {
          status: 429,
          rateLimit: true,
        });
      case "answer":
        emitAnswer(handlers);
        return;
      default:
        return real(payload, handlers, signal);
    }
  }

  // The real api is a ChatApiClient *class instance*, so its methods (search,
  // postFeedback, getArticleSource, resume, getHistory) live on the prototype.
  // An object spread (`{ ...api }`) copies only own-enumerable props and would
  // silently drop every method, breaking the fallback Jonas search, feedback,
  // and resume in dev. A Proxy forwards every untouched member to the real api
  // (bound to it) and overrides only the two streaming entry points.
  const wrapped = new Proxy(api, {
    get(target, prop, receiver) {
      if (prop === "start") {
        return (
          payload: unknown,
          handlers: StreamHandlers,
          signal: AbortSignal,
        ) => run(target.start.bind(target), payload, handlers, signal);
      }
      if (prop === "continue") {
        return (
          payload: unknown,
          handlers: StreamHandlers,
          signal: AbortSignal,
        ) => run(target.continue.bind(target), payload, handlers, signal);
      }
      const value = Reflect.get(target, prop, receiver);
      return typeof value === "function" ? value.bind(target) : value;
    },
  });

  // Pre-arm from the URL convenience parameter.
  const search = window.location?.search ?? "";
  const mode = new URLSearchParams(search).get("simulateOutage");
  if (mode === "1" || mode === "sse" || mode === "ai") sticky = "outage";
  else if (mode === "once") queue = ["outage"];
  else if (mode === "network") sticky = "outage-network";

  const dev = {
    script(directives: Directive[]) {
      queue = [...directives];
    },
    set(directive: Directive) {
      sticky = directive;
    },
    once(directive: Directive) {
      queue = [directive, ...queue];
    },
    clear() {
      queue = [];
      sticky = null;
    },
    answer(opts: Partial<AnswerConfig> = {}) {
      answer = { ...answer, ...opts };
    },
    state() {
      const probe = (window as unknown as { __jonasChatState?: () => unknown })
        .__jonasChatState;
      return probe
        ? probe()
        : { note: "attach window.__jonasChatState to expose store state" };
    },
    help() {
      // eslint-disable-next-line no-console
      console.info(
        "__jonasChatDev: script([..]), set(d), once(d), clear(), answer({text,sources}), state(). directives: outage, outage-network, rejected, chat-unavailable, rate-limit, answer, passthrough",
      );
    },
  };
  (window as unknown as { __jonasChatDev?: unknown }).__jonasChatDev = dev;
  return wrapped;
}
